import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardActionArea, Chip, CircularProgress, Typography } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import PageHeader from '../components/common/PageHeader';
import { diskussionService } from '../services/diskussionService';
import type { DiskussionKategorie } from '../types';

function activityLabel(iso: string | null): string {
  if (!iso) return 'Keine Aktivität';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Keine Aktivität';
  return formatDistanceToNow(d, { addSuffix: true, locale: de });
}

function countLabel(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

const DiskussionHomePage: React.FC = () => {
  const [rows, setRows] = useState<DiskussionKategorie[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const data = await diskussionService.listKategorien();
        if (!c) setRows(data);
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title="Diskussion"
        subtitle="Interner Austausch nach Kategorien. Neue Beiträge der letzten Woche sind markiert. Verweise im Text: [[aio:audit:123]], Erwähnungen: @m42."
        breadcrumbs={[{ label: 'Diskussion' }]}
      />
      {rows.length === 0 ? (
        <Typography color="text.secondary">Noch keine Kategorien vorhanden.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {rows.map((k) => {
            const hasNew = k.new_threads_count > 0 || k.new_posts_count > 0;
            return (
              <Card key={k.id} variant="outlined" sx={{ minWidth: 0 }}>
                <CardActionArea
                  component={RouterLink}
                  to={`/diskussion/kategorie/${k.id}`}
                  sx={{ alignItems: 'stretch' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 2,
                      minWidth: 0,
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                    }}
                  >
                    <ForumIcon color="primary" sx={{ mt: 0.4, flexShrink: 0 }} />
                    <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                      <Typography variant="h6" component="h2" sx={{ overflowWrap: 'anywhere', lineHeight: 1.3 }}>
                        {k.name}
                      </Typography>
                      {k.beschreibung && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            overflowWrap: 'anywhere',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {k.beschreibung}
                        </Typography>
                      )}
                      {hasNew && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                          <Chip
                            size="small"
                            color="primary"
                            label={`Neu: ${countLabel(k.new_threads_count, 'Thread', 'Threads')}, ${countLabel(k.new_posts_count, 'Beitrag', 'Beiträge')}`}
                            sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 2, sm: 3 },
                        flexShrink: 0,
                        flexWrap: 'wrap',
                        ml: { md: 'auto' },
                        pt: 0.25,
                      }}
                    >
                      <Box sx={{ minWidth: 72 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {k.threads_count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {k.threads_count === 1 ? 'Thread' : 'Threads'}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 72 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {k.posts_count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {k.posts_count === 1 ? 'Beitrag' : 'Beiträge'}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 120, maxWidth: 180 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                          {activityLabel(k.latest_activity_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Letzte Aktivität
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default DiskussionHomePage;
